import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Instagram } from 'lucide-react';
import FadeIn from '../components/FadeIn';
import { useSiteData } from '../contexts/SiteDataContext';

const HeroSection = ({ artistInfo, heroImage }) => (
  <section className="relative h-[65vh] md:h-[80vh] overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }}>
      <div className="absolute inset-0 bg-black/40" />
    </div>
    <div className="relative z-10 h-full flex items-center justify-center text-center px-6">
      <div>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-light tracking-[0.02em] text-white mb-4" data-testid="hero-title">
          {artistInfo.name}
        </h1>
        <p className="text-[13px] md:text-[14px] tracking-[0.05em] text-white/60 font-light">
          {artistInfo.tagline}
        </p>
        <div className="mt-10">
          <Link
            to="/about"
            data-testid="hero-discover-link"
            className="inline-flex items-center gap-2.5 text-[12px] tracking-[0.03em] text-white/70 hover:text-white transition-colors duration-400 border-b border-white/25 hover:border-white/50 pb-1"
          >
            Discover the artist
            <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const CollectionCard = ({ collection, index, hoveredIndex, onHover, onLeave }) => (
  <FadeIn delay={index * 80} className={index === 0 ? 'md:col-span-2' : ''}>
    <Link
      to={`/artwork/${collection.id}`}
      data-testid={`collection-card-${collection.id}`}
      className={`group relative block overflow-hidden ${index === 0 ? 'aspect-[16/8]' : 'aspect-[4/3]'}`}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={collection.image}
          alt={collection.title}
          className={`w-full h-full object-cover transition-transform duration-[1.4s] ease-out ${
            hoveredIndex === index ? 'scale-[1.03]' : 'scale-100'
          }`}
        />
      </div>
      <div className={`absolute inset-0 transition-all duration-700 ${hoveredIndex === index ? 'bg-black/40' : 'bg-black/15'}`} />
      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <h3 className="text-[15px] md:text-[17px] font-medium text-white tracking-[0.01em]">{collection.title}</h3>
        <div className={`flex items-center gap-3 mt-2 text-[11px] tracking-[0.02em] text-white/60 transition-all duration-500 ${
          hoveredIndex === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}>
          <span>{collection.pieces.length} works</span>
          <span className="text-white/30">|</span>
          <span className="flex items-center gap-1">View collection <ArrowRight size={11} /></span>
        </div>
      </div>
    </Link>
  </FadeIn>
);

const InstagramSection = ({ artistInfo, instagramImages }) => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="instagram-section">
    <FadeIn>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Instagram size={18} className="text-[#888]" />
          <h2 className="text-[15px] font-light text-[#1a1a1a]">Follow along</h2>
        </div>
        <a
          href={artistInfo.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[12px] text-[#999] hover:text-[#1a1a1a] transition-colors"
        >
          @smclaughlin.artist
          <ArrowRight size={12} />
        </a>
      </div>
    </FadeIn>
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
      {instagramImages.map((img, idx) => (
        <FadeIn key={img} delay={idx * 50}>
          <a
            href={artistInfo.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="block aspect-square overflow-hidden bg-[#f5f5f5] group"
          >
            <img src={img} alt={`Studio work ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-[1.05] group-hover:opacity-90 transition-all duration-700" />
          </a>
        </FadeIn>
      ))}
    </div>
  </section>
);

const Home = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { artistInfo, collections, instagramImages, isLoading } = useSiteData();

  if (isLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[13px] text-[#aaa]" data-testid="home-loading">Loading…</div>;
  }

  const heroImage = collections[0]?.image;

  return (
    <div data-testid="home-page">
      <HeroSection artistInfo={artistInfo} heroImage={heroImage} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <FadeIn>
          <div className="mb-14">
            <h2 className="text-xl md:text-2xl font-light text-[#1a1a1a] tracking-[0.01em]">Selected works</h2>
            <div className="w-10 h-px bg-[#ddd] mt-5" />
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {collections.map((collection, index) => (
            <CollectionCard
              key={collection.id}
              collection={collection}
              index={index}
              hoveredIndex={hoveredIndex}
              onHover={() => setHoveredIndex(index)}
              onLeave={() => setHoveredIndex(null)}
            />
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
        <FadeIn>
          <blockquote className="text-lg md:text-xl lg:text-[22px] leading-[1.7] text-[#555] font-light italic">
            "Whether capturing the subtle gestures of a solitary figure or the quiet beauty of a passing landscape, the work invites reflection on the shared emotional language that binds us all."
          </blockquote>
          <div className="mt-10">
            <Link
              to="/contact"
              data-testid="home-inquire-link"
              className="inline-flex items-center gap-2.5 text-[12px] tracking-[0.02em] text-[#888] hover:text-[#1a1a1a] transition-colors duration-400 border border-[#ddd] hover:border-[#aaa] px-7 py-3"
            >
              Inquire about artwork
              <ArrowRight size={13} />
            </Link>
          </div>
        </FadeIn>
      </section>

      <InstagramSection artistInfo={artistInfo} instagramImages={instagramImages} />
    </div>
  );
};

export default Home;
